// common
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "@/store";
import router from "@/router";

// api
import {
  getRefreshUserTokens,
  getUserToken,
  loginWithPin,
} from "@/api/auth/auth";
import axios from "axios";

// interfaces
import { IAuthState } from "@/interfaces/store/IAuth";
import { ILoginData, IPinLoginData } from "@/interfaces/_shared/auth";

// helpers
import {
  removeUserTokens,
  getUserRefreshToken,
  setUserTokens,
} from "@/utils/helpers/auth";
import { hideIntercom } from "@/utils/helpers/store";
import NotificationService from "@/services/notification.service";
import { LogService } from "@/services/logging";
import { AuthErrors } from "@/constants/errors/network";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { processAdditionalEvents } from "@/utils/helpers/compliance";
import { EventType } from "@smeetz/compliance-common";

@Module({ dynamic: true, namespaced: true, store, name: "Auth" })
class Auth extends VuexModule implements IAuthState {
  public loading = false;
  public token = "";
  public showingSessionExpiryPopup = false;

  public get getToken(): string | undefined {
    return this.token;
  }

  public get getIsAuth(): boolean {
    return Boolean(this.token) && router.currentRoute.value.path !== "/login";
  }

  public get isInBooking(): boolean {
    return router.currentRoute.value.path === "/booking";
  }

  public get cognitoId(): string {
    const accessToken = String(this.getToken);
    const cognitoUserData = JSON.parse(atob(accessToken.split(".")[1]));
    if (cognitoUserData && cognitoUserData.sub) {
      return cognitoUserData.sub;
    }
    return "";
  }

  // public get getSmtzAuthToken(): string | undefined {
  //   return this.smtzAuthToken;
  // }

  @Action({ commit: "SET_TOKEN", rawError: true })
  public setAccessToken(token: string) {
    return token;
  }

  @Action({ commit: "SET_TOKEN", rawError: true })
  public async Login(userData: ILoginData): Promise<string> {
    try {
      const dataToken = await getUserToken(userData);

      // this.SET_TOKEN(tokenResponse.access_token);
      setUserTokens(dataToken);

      return dataToken.access_token;
    } catch (error) {
      LogService.info("Login failed", { error });
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        NotificationService.showAuthError();
        throw AuthErrors.InvalidCredentials;
      }
      NotificationService.showCustomError(t("error.no-internet"));
      throw AuthErrors.UnknownError;
    }
  }

  @Action({ commit: "SET_TOKEN", rawError: true })
  public async LoginWithPin(data: IPinLoginData): Promise<string> {
    try {
      const dataToken = await loginWithPin(data);

      setUserTokens(dataToken);

      return dataToken.access_token;
    } catch (error) {
      LogService.info("PIN Login failed", { error });
      if (axios.isAxiosError(error) && error.response) {
        // pos-signin returns structured errors, key the in-screen retry UX on
        // the status and keep the message text as a fallback.
        const status = error.response.status;
        const backendError = (
          (error.response.data as { error?: string })?.error || ""
        ).toLowerCase();
        // Rate-limit lockout after too many failed attempts (429).
        if (status === 429 || backendError.includes("too many requests")) {
          throw "rate-limited";
        }
        // Bad PIN (400 { error: "Invalid credentials." }).
        if (status === 400 || backendError.includes("invalid credentials")) {
          throw "wrong-pin";
        }
        // Other backend errors (auth-flow issues)
        NotificationService.showGeneralResponseError();
        throw AuthErrors.UnknownError;
      }
      NotificationService.showCustomError(t("error.no-internet"));
      throw AuthErrors.UnknownError;
    }
  }

  @Action({ commit: "SET_TOKEN", rawError: true })
  public async SetTokensAfterRefresh(): Promise<string> {
    const userData = {
      refresh_token: getUserRefreshToken(),
    };
    // lets remove user tokens to avoid the request interceptor getting into an infinite loop
    removeUserTokens();
    const dataToken = await getRefreshUserTokens(userData);

    await setUserTokens(dataToken);

    return dataToken.access_token;
  }

  @Action({ commit: "SET_TOKEN" })
  public UserLogOut(
    data: { signWorkOutEvent: boolean } = { signWorkOutEvent: true }
  ) {
    removeUserTokens();
    // Skipped when the login never got as far as WORK_IN: signing a WORK_OUT
    // for a session that never opened is rejected by the fiscal provider.
    if (data.signWorkOutEvent) {
      processAdditionalEvents(EventType.WORK_OUT).catch((error) => {
        LogService.info("Error processing WORK OUT event", {
          error,
        });
      });
    }
    hideIntercom();
    return "";
  }

  @Mutation
  private SET_TOKEN(payload: string) {
    this.token = payload;
  }

  @Mutation
  public setShowingSessionExpiryPopup(value: boolean) {
    this.showingSessionExpiryPopup = value;
  }
}

const AuthModule = getModule(Auth);
export default AuthModule;
